import {
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@lytra/frontend-common/components/v2/ui/button";
import { cn } from "@/lib/utils";

interface ClampedTextProps {
  /** The text nodes (and chips) to show. Never HTML: the caller renders React text. */
  children: ReactNode;
  /** Changes whenever the text does, so the overflow is measured again. */
  textKey: string;
  /** Clamp to 12 lines with a "Mehr anzeigen" toggle. False shows the text whole. */
  clamp?: boolean;
}

/**
 * Running text clamped at 12 lines with the phase 2 "Mehr anzeigen" / "Weniger anzeigen"
 * toggle, shared by a note's body and a send entry's text. `whitespace-pre-wrap` keeps the
 * writer's line breaks and `break-words` keeps an unbroken string inside the card. The
 * toggle appears only once the laid-out text really overflows.
 */
export function ClampedText({
  children,
  textKey,
  clamp = true,
}: ClampedTextProps) {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  const [expanded, setExpanded] = useState(false);
  const [overflowing, setOverflowing] = useState(false);
  const clamped = clamp && !expanded;

  // Only a clamped body can be measured: expanded, the toggle stays once it has been shown.
  const measure = useCallback(() => {
    const el = ref.current;
    if (el && el.scrollHeight > el.clientHeight) setOverflowing(true);
  }, []);

  // Measuring the laid-out DOM, which render cannot know.
  /* eslint-disable react-hooks/set-state-in-effect */
  useLayoutEffect(() => {
    if (!clamped) return;
    setOverflowing(false);
    measure();
    const el = ref.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [clamped, textKey, measure]);
  /* eslint-enable react-hooks/set-state-in-effect */

  return (
    <div className="flex min-w-0 flex-col items-start gap-1">
      <div
        ref={ref}
        data-slot="note-body"
        className={cn(
          "min-w-0 max-w-full whitespace-pre-wrap break-words text-body text-ink",
          clamped && "line-clamp-[12]",
        )}
      >
        {children}
      </div>
      {clamp && overflowing && (
        <Button
          variant="ghost"
          size="sm"
          aria-expanded={expanded}
          onClick={() => setExpanded((value) => !value)}
        >
          {expanded ? t("tickets.notes.showLess") : t("tickets.notes.showMore")}
        </Button>
      )}
    </div>
  );
}
